/**
 * Profile — rebuilt for WP7 against prototypes/profile-plan.html figs 1, 2
 * and 4. Structure: the student app's profile band (bg-primary, back + title
 * row, then avatar/name/phone), then Your plan / Account / About as
 * design-system row lists instead of the old stacked form cards.
 *
 * Single parent account per household (decision 53, revises 43/44): the
 * join-by-code card, the Family managers row, the make-a-manager button and
 * the "only <owner> can cancel" notice are all gone from this page — and the
 * join-by-code endpoint itself (`POST /api/household/join`) is gone
 * server-side too, not just unreachable. `grantHouseholdAdmin` still exists,
 * now as an admin transfer rather than an add.
 *
 * What moved rather than changed (PPRF-017 — no behaviour loss):
 *  - name/phone editing → ParentDetailsSheet, off the "Your details" row
 *    (the identity head carries no edit affordance — user decision);
 *  - Delete account → a muted footer link under the version, opening
 *    DeleteAccountSheet; red lives only inside that sheet (PPRF-021);
 *  - Log out → a row here, out of RootLayout's DetailHeader, which this page
 *    now replaces entirely (`ownsHeader`).
 * Household, access and manage-subscription are carried over as-is.
 *
 * A free-tier household gets a gate card naming what's locked instead of the
 * plan rows (PPRF-019). The in-app plan page (PPRF-018) is NOT built here —
 * "Manage subscription" still opens the store, and "Upgrade now" still opens
 * the existing paywall.
 */

import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { associateHousehold } from '@tupai/analytics/client';
import { useAuthStore } from '@/store/auth-store';
import { identityHue } from '@/lib/child-identity';
import { useLinksStore } from '@/store/links-store';
import {
  deleteAccount,
  getAccess,
  getHousehold,
  ApiError,
  type AccessResponse,
  type HouseholdResponse,
} from '@/lib/api';
import ParentDetailsSheet from '@/components/parent-details-sheet';
import DeleteAccountSheet from '@/components/delete-account-sheet';
import AppLanguageSheet, { LANGUAGE_LABELS } from '@/components/app-language-sheet';
import { useTranslation } from '@tupai/i18n/react';
import { useLanguage } from '@/lib/language';
import { fmtDayMonth } from '@/lib/format';
import { planName, planStatusLine, pendingPlanLabel } from '@/lib/plan-copy';
import {
  IconChevronLeft,
  IconChevronRight,
  IconUser,
  IconCreditCard,
  IconFileText,
  IconShieldLock,
  IconLogout,
  IconExternalLink,
  IconLanguage,
} from '@tabler/icons-react';
import OverLimitBanner from '@/components/over-limit-banner';

export const Route = createFileRoute('/_protected/profile')({
  component: ProfilePage,
});

// planName/planStatusLine/pendingPlanLabel moved to lib/planCopy.ts (WP2) —
// the plan-name and status rules for the "Your plan" card below.

function SectionLabel({ children }: { children: string }) {
  return <p className="text-xs font-bold uppercase tracking-wide text-muted">{children}</p>;
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="divide-y divide-border-light overflow-hidden rounded-lg border border-border bg-surface">
      {children}
    </div>
  );
}

// One row recipe for every list item on this page — icon, label, optional
// value, optional trailing glyph. `as` keeps a plain row from becoming a
// button when there's nothing to tap.
function Row({
  Icon,
  label,
  value,
  onClick,
  href,
  trailing = 'chevron',
  danger,
}: {
  Icon: typeof IconUser;
  label: string;
  value?: string;
  onClick?: () => void;
  href?: string;
  trailing?: 'chevron' | 'external' | 'none';
  danger?: boolean;
}) {
  const inner = (
    <>
      <Icon
        size={18}
        stroke={2}
        className={`shrink-0 ${danger ? 'text-error' : 'text-muted'}`}
        aria-hidden
      />
      <span className={`flex-1 text-sm font-bold ${danger ? 'text-error' : 'text-content'}`}>
        {label}
      </span>
      {value && <span className="shrink-0 text-sm text-muted">{value}</span>}
      {trailing === 'chevron' && (
        <IconChevronRight size={16} stroke={2} className="shrink-0 text-hint" aria-hidden />
      )}
      {trailing === 'external' && (
        <IconExternalLink size={15} stroke={2} className="shrink-0 text-hint" aria-hidden />
      )}
    </>
  );
  const cls = 'flex w-full min-h-11 items-center gap-3 px-4 py-3 text-left';
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={`${cls} cursor-pointer`}>
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button onClick={onClick} className={`${cls} cursor-pointer transition-colors hover:bg-bg`}>
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
}

function ProfilePage() {
  const navigate = useNavigate();
  const router = useRouter();
  const { parentId, parentName, phone, email, logout } = useAuthStore();
  const { showToast, parentLinks } = useLinksStore();
  // Same input as TopBar's plate (the stored parent name, 'P' fallback) so
  // this plate and the top bar's agree on one colour.
  const hue = identityHue(parentName || 'P');
  const locale = useLanguage();
  const { t } = useTranslation('account');

  const [household, setHousehold] = useState<HouseholdResponse['household']>(null);
  const [access, setAccess] = useState<AccessResponse | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const refreshHousehold = () => {
    getHousehold()
      .then((r) => {
        setHousehold(r.household);
        // Opportunistic, not startup-blocking (Phase 2.7, B2): getHousehold()
        // is only ever fetched lazily by this page and PaywallPage, so this
        // is the earliest honest point an id exists client-side — no
        // dedicated post-login fetch was added for it (ground rule 1).
        if (r.household?.id) associateHousehold(r.household.id);
      })
      .catch(() => {});
    getAccess()
      .then(setAccess)
      .catch(() => {});
  };

  useEffect(refreshHousehold, []);

  const iAmBillingOwner =
    household?.members.some((m) => m.is_billing_owner && m.parent_id === parentId) ?? false;
  const onFreeTier = !access || access.seat_tier === null;

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await deleteAccount();
      navigate({ to: '/login', replace: true });
      logout();
    } catch (err: unknown) {
      let message: string = t('errDeleteFailed');
      if (err instanceof ApiError && err.status === 409) {
        if (err.code === 'children_still_linked') {
          message = t('errChildrenLinked');
        } else if (err.code === 'billing_owner_active') {
          message = t('errBillingOwnerActive');
        }
      }
      showToast('error', message);
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  const childNames = parentLinks
    .filter((l) => l.status === 'approved')
    .map((l) => l.other_party_name)
    .filter(Boolean) as string[];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface">
      {/* Header + identity — one continuous bg-primary band, the student
          app's profile recipe (student-client routes/profile/index.tsx): a h-14 row
          carrying the back control and the page title, then the identity
          block, then a plain bg-surface body. `primary` is the parent blue
          override, so this is the blue band the WP0 prototype specified;
          an earlier pass dropped it for a white head, which left the page
          with no title at all.
          From `lg` Profile is a sidebar item (RootLayout's navHiddenBelowLg),
          not a screen reached by a back chevron, so the band becomes a
          rounded card level with the rail's first card instead — `lg:mt-4`
          matches AppShell's `lg:pt-4` on the rail region so the two tops
          line up. Phone/tablet stays pixel-identical. */}
      <div className="bg-primary safe-area-top shrink-0 lg:mt-4 lg:overflow-hidden lg:rounded-lg">
        <div className="flex h-14 shrink-0 items-center gap-2 px-4">
          <button
            aria-label={t('back')}
            onClick={() => router.history.back()}
            // The sidebar is the way out from `lg` — no back control needed
            // once this card isn't a "screen" you navigated into anymore.
            className="-ml-2 flex min-h-11 min-w-11 cursor-pointer items-center justify-center text-surface lg:hidden"
          >
            <IconChevronLeft size={22} stroke={2.5} />
          </button>
          <p className="flex-1 truncate text-base font-bold text-surface">{t('profile')}</p>
        </div>

        <div className="flex items-center gap-3 px-4 pb-6 pt-1">
          <div
            className={`flex size-16 shrink-0 items-center justify-center rounded-pill border-4 border-surface ${hue.fill} text-lg font-bold ${hue.label}`}
          >
            {(parentName || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-display text-2xl text-surface">{parentName}</p>
            {phone && <p className="mt-0.5 truncate text-sm font-semibold text-surface">{phone}</p>}
            {email && <p className="mt-0.5 truncate text-sm font-semibold text-surface">{email}</p>}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 py-5">
        {/* Over seat cap — only reachable via a downgrade made outside the
            app (the in-app paywall blocks this before it ever happens).
            Surfaced proactively here and on My children, not just reactively
            when linking is attempted (decision 49, TUP-1864). */}
        <OverLimitBanner household={household} onChanged={refreshHousehold} />

        {/* Syllabus over tier (claim 7's out-of-app half) — same story as
            the seat-cap card above: only reachable via a downgrade made
            outside the app (PaywallPage blocks this in-app), surfaced
            proactively rather than waiting for the child to hit a gate
            somewhere else. Both cards can show at once — they're
            independent mismatches. */}
        {household?.syllabus_over_tier && (
          <div className="rounded-lg border-[1.5px] border-error/20 bg-error/5 p-4 flex flex-col gap-2">
            <h2 className="text-sm font-extrabold text-content">{t('syllabusOverTierTitle')}</h2>
            <p className="text-sm font-semibold text-muted">{t('syllabusOverTierBody')}</p>
            {/* Upgrading is now the only resolution the PARENT can carry out
                on their own — since decision 50 was reversed they can't move
                the child's syllabus, so it leads. "Manage children" stays as
                the secondary because unlinking the child also clears the
                mismatch, but it is no longer the thing to reach for first. */}
            <div className="flex gap-2 mt-1">
              <button
                onClick={() =>
                  navigate({ to: '/paywall', state: { trigger: 'syllabus_over_tier' } })
                }
                className="flex-1 min-h-11 rounded-md font-bold text-sm bg-error text-white cursor-pointer"
              >
                {t('upgradeNow')}
              </button>
              <button
                onClick={() => navigate({ to: '/children' })}
                className="flex-1 min-h-11 rounded-md font-bold text-sm bg-surface text-muted border border-border cursor-pointer"
              >
                {t('manageChildren')}
              </button>
            </div>
          </div>
        )}

        {/* Family plan — money first, it gets the care. Stays in the column
            at every width: the free-tier gate copy, the billing owner's
            Manage subscription row, and the pending-downgrade line are only
            ever rendered here (see planCopy.ts and the Row below) — the
            rail's Profile card carries children only (RootLayout). */}
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('sectionYourPlan')}</SectionLabel>

          {onFreeTier ? (
            // Gate card: name what's locked, never imply a plan is active.
            <div className="rounded-lg border border-border bg-surface p-4">
              <p className="text-sm font-bold text-content">{t('onFreePlan')}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted">{t('onFreePlanBody')}</p>
              {/* Upgrading from the free tier goes through the same
                  what-you-get screen onboarding uses — this is the one CTA
                  here whose audience hasn't seen it. "Change plan" and
                  "Manage subscription" below stay on /paywall: their audience
                  already owns a plan, and OnboardingPlansPage skips itself for
                  anyone entitled, which would bounce them to /children.
                  `from` tells that screen's X to come back here. */}
              <button
                onClick={() => navigate({ to: '/onboarding/plans', state: { from: '/profile' } })}
                className="cta-primary mt-3"
              >
                {t('upgradeNow')}
              </button>
            </div>
          ) : (
            <Card>
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-content">{planName(access, t)}</p>
                  <p className="mt-0.5 text-sm text-muted">{planStatusLine(access, t, locale)}</p>
                  {/* Pending deferred downgrade (decision 66). The plan name
                      above deliberately still shows the CURRENT tier — the
                      household keeps it until the period ends — so this is the
                      only place the change is visible after the confirmation
                      screen. Rendered in `primary`, not `muted`: it is the one
                      line on this card that is about the future.
                      Falls back to the dateless wording when the store gave us
                      no date (Google Play's DEFERRED gives none). */}
                  {access &&
                    (() => {
                      const pending = pendingPlanLabel(access);
                      if (!pending) return null;
                      return (
                        <p className="mt-1 text-sm font-semibold text-primary">
                          {access.pending_effective_at
                            ? t('planChangingOn', {
                                tierName: pending,
                                date: fmtDayMonth(access.pending_effective_at, locale),
                              })
                            : t('planChangingSoon', { tierName: pending })}
                        </p>
                      );
                    })()}
                </div>
                <button
                  onClick={() => navigate({ to: '/paywall', state: { trigger: 'manage_plan' } })}
                  className="min-h-11 shrink-0 cursor-pointer px-2 text-sm font-bold text-primary transition-opacity hover:opacity-70"
                >
                  {access?.status === 'payment_pending' ? t('startPlan') : t('changePlan')}
                </button>
              </div>

              {iAmBillingOwner && (
                <Row
                  Icon={IconCreditCard}
                  label={t('manageSubscription')}
                  onClick={() =>
                    navigate({ to: '/paywall', state: { trigger: 'manage_subscription' } })
                  }
                />
              )}
            </Card>
          )}
        </div>

        {/* Account and About are two short, equal-weight sections (3 rows,
            2 rows) — stacking them leaves a long thin page from md up, so
            they sit side by side from md. `contents` on phone keeps this
            wrapper out of the flow entirely, so both sections stay plain
            siblings in the existing gap-5 column exactly as before. */}
        <div className="contents md:grid md:grid-cols-2 md:gap-4 md:items-start">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('sectionAccount')}</SectionLabel>
            <Card>
              <Row Icon={IconUser} label={t('yourDetails')} onClick={() => setDetailsOpen(true)} />
              <Row
                Icon={IconLanguage}
                label={t('language')}
                value={LANGUAGE_LABELS[locale]}
                onClick={() => setLanguageOpen(true)}
              />
              <Row Icon={IconLogout} label={t('logOut')} onClick={logout} trailing="none" />
            </Card>
          </div>

          <div className="flex flex-col gap-2">
            <SectionLabel>{t('sectionAbout')}</SectionLabel>
            <Card>
              <Row
                Icon={IconFileText}
                label={t('termsOfService')}
                href="https://tupai.ai/terms-of-service/"
                trailing="external"
              />
              <Row
                Icon={IconShieldLock}
                label={t('privacyPolicy')}
                href="https://tupai.ai/privacy-policy/"
                trailing="external"
              />
            </Card>
          </div>
        </div>

        <div className="safe-area flex flex-col items-center gap-1 pt-1">
          <p className="text-sm font-bold text-hint">
            {t('appVersion', {
              version: (import.meta.env.VITE_APP_VERSION as string) ?? '0.0.0',
            })}
          </p>
          <button
            onClick={() => setDeleteOpen(true)}
            className="min-h-11 cursor-pointer text-sm font-semibold text-muted underline underline-offset-4"
          >
            {t('deleteAccount')}
          </button>
        </div>
      </div>

      <ParentDetailsSheet open={detailsOpen} onClose={() => setDetailsOpen(false)} />
      <AppLanguageSheet open={languageOpen} onClose={() => setLanguageOpen(false)} />
      <DeleteAccountSheet
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteAccount}
        onManageChildren={() => {
          setDeleteOpen(false);
          navigate({ to: '/children' });
        }}
        deleting={isDeleting}
        childNames={childNames}
      />
    </div>
  );
}
